import { useState, useCallback, useEffect } from "react";
import styled from "styled-components"
import { useApolloClient } from "@apollo/client";
import { GQL_REMIX_RECIPE } from "~/graphql/recipes";
import { getLogger } from "~/utils/logging";
import { useRouter } from "next/router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagicWandSparkles } from "@fortawesome/free-solid-svg-icons";
import useAuthN from "~/hooks/use-authn";
import { AuthenticationStatus } from "~/contexts/authn";

interface RecipeRemixProps {
  recipeid: string
  onComplete?: VoidFunction
}

interface RemixState {
  isRemixing: boolean;
  prompt: string;
}

const DefaultRemixState: RemixState = {
  isRemixing: false,
  prompt: "",
};

const logger = getLogger("RecipeRemixInput")

const RecipeRemixInput = (props: RecipeRemixProps) => {
  const [remixState, setRemixState] = useState<RemixState>(DefaultRemixState);
  const client = useApolloClient();
  const router = useRouter();
  const authn = useAuthN();

  const doRemix: (prompt: string) => Promise<string> = useCallback((prompt) => {
    logger.debug(`begin remix, prompt="${prompt}"`)

    return client.mutate({
      mutation: GQL_REMIX_RECIPE,
      variables: {
        id: props.recipeid,
        prompt: prompt,
      },
    })
      .then((res) => {
        logger.debug(`completed`, res);
        if (!!res.errors?.length) {
          logger.error(`remix failed`, res.errors);
          setRemixState(DefaultRemixState);
          return Promise.reject(res.errors)
        }

        // try to extract the ID
        const jobID: string = res.data?.remixRecipe?.recipeJobId || "";
        if (!jobID) {
          logger.error("malformed response");
          setRemixState(DefaultRemixState);
          return Promise.reject();
        }
        logger.debug(`received jobID`, jobID);

        // return the job ID
        return Promise.resolve(jobID);
      })
  }, [client, props.recipeid])

  // handle remix trigger
  const handleRemix = useCallback(() => {

    if (authn.status !== AuthenticationStatus.LOGGED_IN) {
      // todo: make this redirect back to the same remix prompt?
      router.push(`/sign-in`)
      return;
    }

    // Do nothing if prompt is empty
    if (remixState.prompt.trim() === '') {
      logger.debug(`remix called with empty prompt - ignoring`);
      return;
    }

    doRemix(remixState.prompt)
      .then((jobID) => router.push(`/mixing/${jobID}`))
      .then(() => !!props.onComplete && props.onComplete())
  }, [authn.status, remixState.prompt, doRemix, router, props])

  // start remix on enter key press
  useEffect(() => {
    const handleEnter = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        handleRemix()
      }
    };
    window.addEventListener('keydown', handleEnter);

    return () => {
      window.removeEventListener('keydown', handleEnter);
    };
  }, [remixState.prompt, doRemix, router, props.onComplete, handleRemix]);

  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRemixState((prior) => {
      return { ...prior, prompt: event.target.value }
    })
  }, [])

  const handleFocus = useCallback(() => {
    setRemixState((prior) => ({ ...prior, isRemixing: true }))
  }, [])

  useEffect(() => {
    logger.debug(`route changed`, router.asPath)
    setRemixState(DefaultRemixState)
  }, [router.asPath])

  return (
    <Container
      onFocus={handleFocus}
    >
      <RemixOutline tabIndex={-1} $isActive={remixState.isRemixing}>
        <RemixInput
          value={remixState.prompt}
          onChange={handleInputChange}
          placeholder={`Try "make this vegan"`}
        />
        <RemixButton onClick={() => {
          handleRemix()
        }}>
          <RemixSendIcon>
            <FontAwesomeIcon icon={faMagicWandSparkles} />
          </RemixSendIcon>
        </RemixButton>
      </RemixOutline>
    </Container>
  )
}

const Container = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`

const RemixButton = styled.div`
  cursor: pointer;
`;

const RemixSendIcon = styled.div`
  width: 20px;
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
  padding-left: 5px;
  padding-right: 1rem;
`

const RemixOutline = styled.div<{ $isActive?: boolean }>`
  width: 100%;
  height: 100%;
  border: 1px solid transparent;
  background-color: #f8f8f5;
  display: flex;
  flex-direction: row;
  align-items: center;
  border-radius: 12px;

  border: ${props => props.$isActive ? "1px solid black" : "none"};

  &:focus {
    box-shadow: 0px 0px 2px black;
  }

  &:hover {
    box-shadow: 0px 0px 2px black;
  }
`

const RemixInput = styled.input`
  width: 100%;
  height: calc(90% - 2px);
  background: transparent;
  border: none;
  font-size: 17px;
  padding-left: 1rem;

  &:focus {
    box-shadow: none;
    outline: none;
    border: none;
  }

  &:hover {
    box-shadow: none;
    outline: none;
    border: none;
  }
`

export default RecipeRemixInput;
